import type { MessageKey } from "./en";

/** Portuguese UI catalog. Every key in {@link en} must be present. */
export const pt: Record<MessageKey, string> = {
  "app.title": "Rescript — edite vídeos como edita texto",
  "common.cancel": "Cancelar",
  "common.close": "Fechar",
  "common.delete": "Excluir",
  "common.download": "Baixar",
  "common.import": "Importar",
  "common.loading": "Carregando",
  "common.remove": "Remover",
  "common.restore": "Restaurar",
  "common.retry": "Tentar de novo",
  "common.searchOrCreate": "Buscar ou criar…",
  "common.settings": "Configurações",
  "common.system": "Sistema",
  "common.tools": "Ferramentas",
  "settings.appearance": "Aparência",
  "settings.light": "Claro",
  "settings.dark": "Escuro",
  "settings.interfaceLanguage": "Idioma da interface",
  "settings.privacy": "Privacidade",
  "settings.helpImprove": "Ajude a melhorar o app",
  "settings.telemetryHelp": "Envia estatísticas anônimas de uso de recursos e relatórios de falhas.",
  "settings.support": "Suporte / feedback",
  "settings.reportIssue": "Reportar um problema",
  "settings.homepage": "Página inicial",
  "settings.github": "GitHub",
  "settings.followX": "Seguir no X",
  "model.transcriptSource": "Origem da transcrição",
  "model.language": "Idioma",
  "model.transcriptLanguage": "Idioma da transcrição",
  "model.importTranscript": "Importar transcrição",
  "upload.recentProjects": "Projetos recentes",
  "upload.removeRecent": "Remover dos recentes",
  "upload.dropPrefix": "Solte um arquivo de vídeo ou áudio aqui, ou",
  "upload.browse": "navegue",
  "upload.chooseTranscriptFirst": "Escolha uma transcrição no menu acima e depois solte a mídia",
  "upload.willUseTranscript": "Usará {name} · MP4, WebM, MOV, MP3, WAV, …",
  "upload.mediaFormats": "MP4, WebM, MOV, MP3, WAV, M4A, …",
  "upload.gettingReady": "Preparando tudo",
  "upload.gettingReadyHelp": "Configurando o motor de mídia; isso só acontece uma vez.",
  "upload.unsupported": "Este navegador não pode executar o editor",
  "upload.unsupportedHelp":
    "A edição precisa de SharedArrayBuffer, que exige uma página isolada entre origens. Experimente uma versão recente do Chrome, Edge, Safari ou Firefox via HTTPS.",
  "upload.unsupportedSimd": "Este dispositivo não consegue executar o editor",
  "upload.unsupportedSimdHelp": "A edição precisa de WebAssembly SIMD, que este processador não informa suportar. Em um PC com Windows no Arm, atualize para a versão mais recente do Rescript — os instaladores atuais incluem uma versão Arm64 nativa que não roda emulada.",
  "upload.transcribeTitle": "Transcrever",
  "upload.transcribeText": "Whisper localmente, ou importe SRT / VTT.",
  "upload.editTitle": "Editar",
  "upload.editText": "Selecione palavras e pressione Excluir para editar.",
  "upload.exportTitle": "Exportar",
  "upload.exportText": "Renderize o corte final em MP4 ou M4A.",
  "editor.chooseTranscript": "Primeiro escolha um arquivo de transcrição no menu de origem.",
  "editor.chooseMedia": "Escolha um arquivo de vídeo ou áudio.",
  "editor.undo": "Desfazer (⌘Z)",
  "editor.redo": "Refazer (⇧⌘Z)",
  "editor.export": "Exportar",
  "topbar.startOver": "Começar de novo",
  "transcript.replace": "Substituir transcrição a partir de SRT, VTT ou JSON",
  "transcript.header": "Transcrição",
  "transcript.wordDeleted": "{count} palavra",
  "transcript.wordsDeleted": "{count} palavras",
  "transcript.replaceConfirm": "Substituir a transcrição atual por este arquivo?",
  "transcript.invalidFile": "Escolha um arquivo de transcrição SRT, VTT ou JSON.",
  "transcript.noSpeech": "Nenhuma fala detectada. Confirme que este arquivo tem áudio, ou importe uma transcrição.",
  "transcript.cut": "Cortar",
  "transcript.follow": "Seguir cabeçote",
  "transcript.hideDeleted": "Ocultar palavras excluídas",
  "transcript.showDeleted": "Mostrar palavras excluídas",
  "transcript.correct": "Corrigir",
  "transcript.scrollWithPlayhead": "Rolar com o cabeçote",
  "transcript.joinSplit": "Clipe dividido — clique para unir estes clipes",
  "transcript.joinClips": "Unir clipes",
  "transcript.hesitation": "Hesitação detectada (não transcrita) — corte-a com Remover vícios de linguagem",
  "transcript.textLabel": "Texto da transcrição",
  "transcript.textHint": "As setas percorrem as palavras e movem o cursor de reprodução. Shift com as setas seleciona. Backspace ou Delete corta palavras, Enter as corrige, Espaço reproduz ou pausa, e Desfazer recupera os cortes.",
  "transcript.textHesitation": "[hesitação]",
  "transcript.textCut": "Cortado: {text}",
  "transcript.textNothingToCut": "Nada para cortar",
  "transcript.textUndo": "Desfazer",
  "transcript.textRedo": "Refazer",
  "tools.bulk": "Limpezas em massa da transcrição",
  "tools.removeFillers": "Remover vícios de linguagem",
  "tools.removeFillersTitle": "Corta vícios (\"ãh\", \"mmm\", \"...\", …) do vídeo",
  "tools.restoreFillers": "Restaurar vícios de linguagem",
  "tools.restoreFillersTitle": "Devolve cada vício cortado",
  "tools.removeSilences": "Remover silêncios",
  "tools.removeSilencesTitle": "Corta pausas e silêncios (≥{seconds} s) do vídeo",
  "tools.restoreSilences": "Restaurar silêncios",
  "tools.restoreSilencesTitle": "Devolve cada pausa cortada",
  "timeline.removed": "{trimmed} removido — duração original {duration}",
  "timeline.back5": "Voltar 5 s",
  "timeline.playPause": "Reproduzir / pausar (espaço)",
  "timeline.forward5": "Avançar 5 s",
  "timeline.split": "Dividir",
  "timeline.splitTitle": "Dividir clipe no cabeçote (S)",
  "timeline.splitDisabled": "Mova o cabeçote para uma região mantida para dividir",
  "timeline.delete": "Excluir",
  "timeline.deleteTitle": "Excluir clipe selecionado (Excluir)",
  "timeline.deleteDisabled": "Selecione um clipe na linha do tempo para excluí-lo",
  "timeline.restore": "Restaurar",
  "timeline.restoreTitle": "Restaurar corte selecionado (Excluir)",
  "timeline.restoreDisabled": "Selecione um corte ou uma seção de silêncio na linha do tempo para restaurar",
  "timeline.zoomOut": "Afastar",
  "timeline.fit": "Ajustar à janela",
  "timeline.zoomIn": "Aproximar — arraste as bordas das palavras para ajustar o tempo",
  "timeline.joinClips": "Unir estes clipes (remover divisão)",
  "timeline.trimStart": "Recortar início do clipe",
  "timeline.trimEnd": "Recortar final do clipe",
  "timeline.hesitationAdjust": "… hesitação detectada — arraste as bordas para ajustar, ou corte-a com Remover vícios de linguagem",
  "timeline.hesitationCut": "… hesitação detectada — corte-a com Remover vícios de linguagem",
  "timeline.dragTiming": "{word} — arraste as bordas para ajustar o tempo",
  "timeline.scrollZoom": "Role para aproximar / afastar",
  "speaker.moveStart": "Arraste para mover onde este falante começa",
  "speaker.moveLabel": "Mover rótulo do falante",
  "speaker.options": "Opções do falante",
  "speaker.change": "Alterar falante",
  "speaker.rename": "Renomear falante",
  "speaker.renameAction": "Renomear",
  "speaker.replace": "Substituir no projeto por…",
  "speaker.removeProject": "Remover do projeto",
  "speaker.newName": "Novo nome",
  "speaker.find": "Buscar um falante…",
  "speaker.noMatches": "Nenhum falante correspondente",
  "speaker.renameTo": "Renomear {current} para {next}",
  "speaker.button": "Falante",
  "speaker.search": "Buscar ou criar…",
  "speaker.defaultName": "Falante {number}",
  "speaker.create": "Criar \"{name}\"",
  "export.title": "Exportar",
  "export.type": "Tipo de exportação",
  "export.video": "Vídeo",
  "export.audio": "Áudio",
  "export.transcript": "Transcrição",
  "export.subtitles": "Legendas",
  "export.videoUnavailable": "A exportação de vídeo não está disponível para projetos só de áudio",
  "export.noAudio": "Este arquivo não tem faixa de áudio",
  "export.noWordsFirst": "Transcreva ou importe uma transcrição primeiro",
  "export.format": "Formato",
  "export.resolution": "Resolução",
  "export.original": "Original",
  "export.plainText": "TXT",
  "export.markdown": "Markdown",
  "export.statOriginal": "Original",
  "export.statCuts": "Cortes",
  "export.statEdited": "Editado",
  "export.includeTimestamps": "Incluir marcas de tempo",
  "export.speakerLabels": "Incluir nomes dos falantes",
  "export.shortCues": "Legendas curtas (≤5 s)",
  "export.transcriptHelp": "Texto com rótulos de falante e cortes removidos.",
  "export.transcriptHelpTimestamps":
    "Texto com rótulos de falante e cortes removidos. Cada turno começa com o tempo na linha do tempo editada.",
  "export.transcriptHelpPdf":
    "PDF usa uma fonte latina padrão — prefira DOCX para Unicode completo (CJK etc.).",
  "export.subtitlesHelp":
    "SRT e VTT usam a linha do tempo editada (com cortes aplicados). JSON conserva a lista completa de palavras para reimportar.",
  "export.subtitlesHelpShortCues":
    "SRT e VTT usam a linha do tempo editada (com cortes aplicados), divididos em legendas curtas por frase (≤5 s). JSON conserva a lista completa de palavras para reimportar.",
  "export.encodingHelp": "Recodificando com ffmpeg.wasm — arquivos longos demoram um pouco.",
  "export.reexport": "Reexportar com as últimas edições",
  "export.rendering": "Renderizando no seu navegador…",
  "export.downloadFile": "Baixar {name}",
  "export.saveFile": "Salvar {name}…",
  "export.saving": "Salvando…",
  "export.saveDialogTitle": "Salvar exportação",
  "export.savedAs": "Salvo como {name}",
  "export.showInFinder": "Mostrar no Finder",
  "export.showInExplorer": "Mostrar no Explorador",
  "export.showInFolder": "Mostrar na pasta",
  "export.renderingNative": "Renderizando…",
  "export.encodingHelpNative": "Recodificando com ffmpeg — arquivos longos demoram um pouco.",
  "export.downloadFormat": "Baixar .{format}",
  "export.exportFormat": "Exportar {format}",
  "export.timeline": "Linha do tempo",
  "export.timelineTitle": "Exportar uma sequência NLE/DAW (Resolve, Premiere, Final Cut, Pro Tools, Reaper, Blender)",
  "export.nle": "Formato",
  "export.frameRate": "Taxa de quadros",
  "export.timelineHelp":
    "A sequência referencia a mídia original pelo nome do arquivo — vincule-a de novo no NLE após importar.",
  "export.timelineHelpAaf": "AAF do Pro Tools / Logic (somente metadados).",
  "export.timelineHelpFcpx": "FCPXML do Final Cut Pro.",
  "export.timelineHelpPremiere": "XML do Adobe Premiere Pro (xmeml).",
  "export.timelineHelpResolve": "XML do DaVinci Resolve (xmeml).",
  "export.timelineHelpReaper": "Projeto do Reaper (.rpp).",
  "export.timelineHelpSamplitude":
    "EDL do Samplitude (.edl) — também abre diretamente no Reaper.",
  "export.timelineHelpBlender":
    "Projeto de edição de vídeo do Blender (.blend, Blender 5.2+) — salve-o na mesma pasta que a mídia original.",
  "export.aafOverCap":
    "Esta edição tem {count} clipes; AAF admite até {max}. Use Resolve, Premiere, Final Cut, Reaper ou EDL do Samplitude.",
  "export.preparing": "Preparando…",
  "import.reading": "Lendo transcrição…",
  "import.importing": "Importando…",
  "import.failed": "Falha ao importar",
  "import.readingFile": "Lendo arquivo…",
  "import.chooseFileShort": "Escolha um arquivo…",
  "import.chooseFile": "Escolha uma transcrição SRT, VTT ou JSON",
  "import.selected": "{name} selecionado",
  "banner.faster": "Quer transcrições e exportações mais rápidas?",
  "banner.getDesktop": "Obtenha o app de desktop do Rescript",
  "banner.downloadFor": "Baixar para {platform}",
  "banner.dismiss": "Dispensar",
  "social.githubRepo": "Repositório do GitHub",
  "social.discordServer": "Servidor do Discord",
  "social.xProfile": "Perfil do X",
  "globalError.title": "Rescript — algo deu errado",
  "globalError.heading": "Algo deu errado",
  "globalError.body":
    "O editor encontrou um erro inesperado. Seus projetos salvos ainda estão neste dispositivo; ao recarregar devem voltar.",
  "progress.loadingMedia": "Carregando mídia…",
  "progress.loadingMediaEngine": "Carregando motor de mídia…",
  "progress.extractingAudio": "Extraindo áudio…",
  "progress.preparingMedia": "Preparando mídia…",
  "progress.loadingSpeechModel": "Carregando modelo de voz…",
  "progress.loadingSpeechCache": "Carregando modelo de voz do cache…",
  "progress.downloadingSpeech": "Baixando modelo de voz…",
  "progress.gpuFallback": "A GPU foi interrompida — continuando na CPU…",
  "progress.detectingSpeech": "Detectando fala…",
  "progress.transcribing": "Transcrevendo…",
  "progress.loadingAlignCache": "Carregando modelo de alinhamento do cache…",
  "progress.downloadingAlign": "Baixando modelo de alinhamento…",
  "progress.aligning": "Alinhando palavras…",
  "progress.speakers": "Identificando falantes…",
  "error.selectModel": "Selecione um modelo de voz para transcrever.",
  "error.workerCrashed": "O worker de transcrição falhou.",
  "error.mediaEngineNetwork": "Não foi possível carregar o motor de mídia — a conexão caiu. Verifique sua internet e tente de novo.",
  "error.mediaEngineStalled": "O motor de mídia parou de responder e foi reiniciado. Feche outros apps e abas para liberar memória e tente de novo.",
  "error.mediaEngineNotReady": "O motor de mídia ainda não está pronto — recarregue a página e tente de novo.",
  "error.simdUnsupported": "Este processador não suporta WebAssembly SIMD, de que o motor de mídia precisa. Em um PC com Windows no Arm, atualize para a versão mais recente do Rescript — os instaladores atuais incluem uma versão Arm64 nativa que não roda emulada.",
  "error.processFile": "Não foi possível processar este arquivo.",
  "error.extractAudio": "Não foi possível extrair áudio deste arquivo.",
  "error.nothingToExport": "Tudo foi excluído — não há nada para exportar.",
  "error.videoExport": "A exportação falhou ao renderizar o vídeo.",
  "error.audioExport": "A exportação falhou ao renderizar o áudio.",
  "error.exportSave": "Não foi possível salvar o arquivo exportado.",
  "error.diskFull": "Não há espaço livre em disco suficiente para concluir. Libere espaço e tente novamente.",
  "error.export": "Erro ao exportar.",
  "error.timelineExport": "Erro ao exportar a linha do tempo.",
  "error.timelineEmpty": "Não há nada para colocar na linha do tempo.",
  "error.aafTemplate": "Não foi possível carregar o modelo de exportação AAF.",
  "error.emptyTranscript": "Esse arquivo de transcrição está vazio.",
  "error.noTimedWords": "Não foram encontradas palavras com tempo nessa transcrição.",
  "error.parseJson": "Não foi possível analisar essa transcrição JSON.",
  "error.jsonShape": 'JSON deve ser um array de palavras ou { "words": [...] }.',
  "error.noWords": "Não há palavras para exportar.",
  "error.projectMissing": "Esse projeto não está mais salvo.",
  "error.openProject": "Não foi possível abrir esse projeto.",
  "error.removeProject": "Não foi possível remover esse projeto.",
  "error.readTranscript": "Não foi possível ler essa transcrição.",
  "error.clearRecent": "Não foi possível limpar os projetos recentes.",
  "error.modelDownload":
    "Não foi possível terminar de baixar o modelo de voz — a conexão caiu. Verifique sua internet e tente de novo; as partes já baixadas são preservadas.",
  "error.gpuReset": "A transcrição foi interrompida quando a GPU reiniciou (muitas vezes após bloquear a tela). Tente de novo.",
  "confirm.clearRecent": "Remover todos os projetos recentes? As edições salvas serão excluídas.",
};
